import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";

const PageContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 30px 20px;
`;

const Header = styled.header`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 28px;
  color: #333;
  margin-bottom: 10px;
`;

const BackButton = styled.button`
  background-color: #f5f5f5;
  color: #333;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 20px;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const Form = styled.form`
  background-color: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #6ed216;
    box-shadow: 0 0 0 2px rgba(110, 210, 22, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #6ed216;
    box-shadow: 0 0 0 2px rgba(110, 210, 22, 0.2);
  }
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 5px;
`;

const SubmitButton = styled.button`
  background-color: #6ed216;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px 20px;
  font-size: 16px;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.3s;

  &:hover {
    background-color: #5cb70f;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorContainer = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 15px;
  border-radius: 5px;
  text-align: center;
  margin-bottom: 20px;
`;

const SuccessContainer = styled.div`
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 15px;
  border-radius: 5px;
  text-align: center;
  margin-bottom: 20px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 18px;
  color: #666;
`;

function EditUserPage({ user, onLogout }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState({
    username: "",
    xandrUsername: "",
    xandrPassword: "",
    role: "salesperson",
    salesPersonName: "",
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const apiUrl =
    process.env.REACT_APP_API_URL || "https://report-campaign.sirdata.io";

  useEffect(() => {
    // Vérifier si l'utilisateur est admin
    if (user && user.role !== "admin") {
      navigate("/");
      return;
    }

    fetchUserData();
  }, [user, userId, navigate]);

  const fetchUserData = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${apiUrl}/api/auth/users/${userId}`);
      const { user } = response.data;

      setUserData({
        username: user.username || "",
        xandrUsername: user.xandrUsername || "",
        xandrPassword: "", // Ne pas afficher le mot de passe existant
        role: user.role || "salesperson",
        salesPersonName: user.salesPersonName || "",
      });
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        navigate("/login");
      } else {
        setError(
          `Erreur lors du chargement des données utilisateur: ${err.message}`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      // Vérifier si le nom du commercial est fourni pour un commercial
      if (userData.role === "salesperson" && !userData.salesPersonName) {
        setError("Le nom du commercial est requis pour un compte commercial");
        setSubmitting(false);
        return;
      }

      const response = await axios.put(
        `${apiUrl}/api/auth/users/${userId}`,
        userData
      );

      if (response.data.success) {
        setSuccess("Utilisateur mis à jour avec succès!");
        setTimeout(() => {
          navigate("/admin/users");
        }, 2000);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Erreur lors de la mise à jour de l'utilisateur"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <BackButton onClick={() => navigate("/admin/users")}>← Retour</BackButton>

      <Header>
        <Title>Modifier l'utilisateur</Title>
      </Header>

      {error && <ErrorContainer>{error}</ErrorContainer>}
      {success && <SuccessContainer>{success}</SuccessContainer>}

      {loading ? (
        <LoadingContainer>
          Chargement des données utilisateur...
        </LoadingContainer>
      ) : (
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Nom d'utilisateur</Label>
            <Input
              type="text"
              name="username"
              placeholder="Nom d'utilisateur"
              value={userData.username}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Identifiant Xandr</Label>
            <Input
              type="text"
              name="xandrUsername"
              placeholder="Identifiant Xandr"
              value={userData.xandrUsername}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Mot de passe Xandr</Label>
            <Input
              type="password"
              name="xandrPassword"
              placeholder="Laisser vide pour ne pas modifier"
              value={userData.xandrPassword}
              onChange={handleChange}
            />
            <HelpText>
              Laissez ce champ vide si vous ne souhaitez pas modifier le mot de
              passe
            </HelpText>
          </FormGroup>

          <FormGroup>
            <Label>Rôle</Label>
            <Select name="role" value={userData.role} onChange={handleChange}>
              <option value="salesperson">Commercial</option>
              <option value="admin">Administrateur</option>
            </Select>
          </FormGroup>

          {userData.role === "salesperson" && (
            <FormGroup>
              <Label>Nom du commercial (dans Xandr)</Label>
              <Input
                type="text"
                name="salesPersonName"
                placeholder="Nom exact du commercial dans Xandr"
                value={userData.salesPersonName}
                onChange={handleChange}
                required={userData.role === "salesperson"}
              />
              <HelpText>
                Ce nom doit correspondre exactement au champ
                "salesperson_for_advertiser" dans Xandr
              </HelpText>
            </FormGroup>
          )}

          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? "Mise à jour..." : "Mettre à jour l'utilisateur"}
          </SubmitButton>
        </Form>
      )}
    </PageContainer>
  );
}

export default EditUserPage;
